import { useEffect, useLayoutEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AppId, DesktopLayout, User } from '@omnia/shared';
import { api, shouldRetry } from '../lib/api.ts';
import { tr } from '../lib/i18n.ts';
import { BAR_HEIGHT, dockedHeight } from '../infobar/layout.ts';
import { useInfoBarPrefs } from '../infobar/prefsStore.ts';
import { useInfoBarShortcut } from '../infobar/useInfoBarShortcut.ts';
import { TopBar } from './TopBar.tsx';
import { SettingsWindow } from '../settings/SettingsWindow.tsx';
import { Taskbar } from './Taskbar.tsx';
import { Window } from './Window.tsx';
import { Wallpaper } from '../wallpapers/Wallpaper.tsx';
import { LaunchContext } from './launch.ts';
import { flushPendingLayoutSave, resetLayoutSync, saveLayoutNow, scheduleLayoutSave } from './layoutSync.ts';
import { useDesktop, type WindowState } from './store.ts';

export function Desktop({ user }: { user: User }) {
  const deskRef = useRef<HTMLDivElement>(null);
  const windows = useDesktop((s) => s.windows);
  const layout = useQuery({
    queryKey: ['desktop-layout'],
    queryFn: ({ signal }) => api<DesktopLayout | null>('/api/desktop/layout', { signal }),
    retry: shouldRetry,
    staleTime: Infinity,
  });

  useLayoutEffect(() => {
    const desk = deskRef.current;
    if (!desk) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      useDesktop.getState().setBounds({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(desk);
    return () => observer.disconnect();
  }, []);

  // The store keeps the hydrated flag, so remounting the shell (breakpoint switch) never re-applies an old layout.
  useEffect(() => {
    if (layout.isSuccess) useDesktop.getState().hydrate(layout.data);
  }, [layout.isSuccess, layout.data]);

  useLayoutPersistence();
  useInfoBarShortcut();
  const infobar = useInfoBarPrefs();
  const docked = dockedHeight(infobar);
  // Runs before the desk's resize is observed, so windows keep their place on screen when the strip docks or leaves.
  useLayoutEffect(() => useDesktop.getState().setDeskTop(docked), [docked]);

  return (
    <LaunchContext value={useDesktop.getState().open}>
      <div className="shell" data-mode={infobar.position} style={{ ['--bar-height' as string]: `${BAR_HEIGHT}px` }}>
        <Wallpaper />
        <TopBar />
        <main className="desk" ref={deskRef} aria-label={tr.desk}>
          {(Object.entries(windows) as [AppId, WindowState][]).map(([id, state]) => (
            <Window key={id} id={id} state={state} />
          ))}
        </main>
        <Taskbar user={user} />
        <SettingsWindow />
      </div>
    </LaunchContext>
  );
}

function useLayoutPersistence() {
  useEffect(() => {
    const unsubscribe = useDesktop.subscribe((state, previous) => {
      if (state.revision !== previous.revision) scheduleLayoutSave();
    });
    // A tab that is closed or backgrounded right after a drag must not lose the last move.
    const onHide = () => void saveLayoutNow({ keepalive: true });
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') onHide();
    };
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onVisibility);
      unsubscribe();
      flushPendingLayoutSave();
      resetLayoutSync();
    };
  }, []);
}
